import { Injectable, effect, inject } from '@angular/core';
import {
  CLINIC_THEME_VARIABLE_NAMES,
  ClinicFontId,
  ClinicTheme,
  clinicFontStylesheetUrl,
  clinicThemeVariables,
  parseClinicTheme,
  toolbarThemeColor,
} from '../models/clinic-theme.type';
import { FirebaseAuthService } from './auth/firebase-auth.service';
import { MembershipService } from './auth/membership.service';

/** Last applied clinic theme, so a reload paints it before memberships load. */
const CACHE_KEY = 'ic-clinic-theme';
/** Resolved toolbar colors for the status bar, read by index.html before Angular boots. */
const PWA_COLOR_CACHE_KEY = 'ic-pwa-theme-color';
/** Dev builds paint the toolbar this color until a clinic theme is applied. */
const DEV_TOOLBAR_COLOR = '#6b2436';

/** Theme currently on the page, so a light/dark toggle can recolor the status bar. */
let activeTheme: ClinicTheme | null = null;

/**
 * Overrides Material's primary/secondary/tertiary system colors and fonts with the selected clinic's theme.
 * Only for a signed-in user with a selected clinic; everyone else sees the Interconsulta theme.
 */
@Injectable({ providedIn: 'root' })
export class ClinicThemeService {
  private auth = inject(FirebaseAuthService);
  private membership = inject(MembershipService);
  private applied: string | null = null;

  constructor() {
    this.apply(readCachedTheme());

    effect(() => {
      if (!this.auth.authReady()) {
        return;
      }
      if (!this.auth.isLoggedIn()) {
        this.apply(null);
        writeCachedTheme(null);
        return;
      }
      if (!this.membership.membershipsReady()) {
        return;
      }
      const theme = this.membership.selectedMembership() ? this.membership.selectedBusinessTheme() : null;
      this.apply(theme);
      writeCachedTheme(theme);
    });
  }

  /** Adds the font's Google Fonts stylesheet once. */
  loadFont(id: ClinicFontId): void {
    const linkId = `clinic-font-${id}`;
    if (document.getElementById(linkId)) {
      return;
    }
    const link = document.createElement('link');
    link.id = linkId;
    link.rel = 'stylesheet';
    link.href = clinicFontStylesheetUrl(id);
    document.head.appendChild(link);
  }

  private apply(theme: ClinicTheme | null): void {
    const key = theme
      ? `${theme.light.primary}${theme.light.accent}${theme.dark.primary}${theme.dark.accent}${theme.font ?? ''}`
      : null;
    if (key === this.applied) {
      return;
    }
    this.applied = key;
    const root = document.documentElement;
    const style = root.style;
    root.classList.toggle('clinic-themed', !!theme);
    if (!theme) {
      for (const name of CLINIC_THEME_VARIABLE_NAMES) {
        style.removeProperty(name);
      }
      syncPwaThemeColor(null);
      return;
    }
    if (theme.font) {
      this.loadFont(theme.font);
    }
    for (const [name, value] of Object.entries(clinicThemeVariables(theme))) {
      style.setProperty(name, value);
    }
    syncPwaThemeColor(theme);
  }
}

/**
 * Points the PWA status bar at the toolbar color. Android reads the theme-color meta tag.
 * iOS ignores hex colors there; black-translucent lets the status-bar strip (and the toolbar's
 * safe-area padding) show this same color underneath the clock and battery.
 */
export function syncPwaThemeColor(theme: ClinicTheme | null = activeTheme): void {
  activeTheme = theme;
  const root = document.documentElement;
  const mode = root.classList.contains('theme-dark') ? 'dark' : 'light';
  const devDefault = !theme && root.classList.contains('dev-build');
  const color = devDefault ? DEV_TOOLBAR_COLOR : toolbarThemeColor(theme, mode);
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', color);
  root.style.setProperty('--pwa-theme-color', color);
  try {
    if (theme) {
      localStorage.setItem(
        PWA_COLOR_CACHE_KEY,
        JSON.stringify({
          light: toolbarThemeColor(theme, 'light'),
          dark: toolbarThemeColor(theme, 'dark'),
        })
      );
    } else {
      localStorage.removeItem(PWA_COLOR_CACHE_KEY);
    }
  } catch {
    // Private mode or full storage: the meta tag is still updated for this visit.
  }
}

function readCachedTheme(): ClinicTheme | null {
  try {
    return parseClinicTheme(JSON.parse(localStorage.getItem(CACHE_KEY) || 'null')) ?? null;
  } catch {
    return null;
  }
}

function writeCachedTheme(theme: ClinicTheme | null): void {
  try {
    if (theme) {
      localStorage.setItem(CACHE_KEY, JSON.stringify(theme));
    } else {
      localStorage.removeItem(CACHE_KEY);
    }
  } catch {
    // Private mode or full storage: the theme still applies once memberships load.
  }
}
